import {
  APPROVAL_STATUS_LABELS,
  DEPLOYMENT_STATUS_LABELS,
  EXECUTION_PHASE_LABELS,
  ON_FAILURE_LABELS,
  PREREQUISITE_STATUS_LABELS,
  QA_DISPOSITION_LABELS,
  QA_STATUS_LABELS,
  READINESS_LABELS,
  RELATION_LABELS,
  RELEASE_STATUS_LABELS,
  RELEASE_TYPE_LABELS,
  ROLLBACK_STATUS_LABELS,
  SCOPE_CHANGE_REASONS,
  SEVERITY_LABELS,
  STEP_STATUS_LABELS,
  TEST_STATUS_LABELS,
  VALIDATION_STATUS_LABELS,
} from "../releases/releaseVocabulary";
import { optionsFrom, type GuideChapter } from "./guideTypes";

/**
 * Releases: what is shipping, whether it may, and what happened.
 *
 * <p>The longest chapter because it is the part of the application with the most rules, and the
 * rules are the point. Most of what looks like friction here — a reason demanded, a gate that
 * will not pass — exists so that somebody reading the release in a year can tell what was decided
 * and by whom.
 */
export const RELEASE_CHAPTER: GuideChapter = {
  id: "releases",
  title: "Releases",
  sections: [
    {
      id: "release-basics",
      title: "What a release is",
      where: "A project's Releases tab",
      purpose:
        "A version with agreed scope. It answers three questions: what is going out, whether it "
        + "is safe to send, and who said so. A release starts almost empty and is filled in as "
        + "you learn — you do not need to know the date to create one.",
      examples: [
        {
          title: "From nothing to shipped",
          steps: [
            "Create the release: version 2.1, name \"Payments 2.1\", type Minor.",
            "Add the work it will carry on the Scope tab, or set the release on each item as you "
            + "create it.",
            "When the contents are agreed, Freeze scope. Everyone downstream now plans against "
            + "something fixed, and later changes are recorded with a reason.",
            "Work through the Pre-requisites, record QA results, collect approvals.",
            "Readiness turns green. Move the release through Approve, Schedule, Start deployment.",
            "Work the runbook on the Execution tab, record the deployment, then run the "
            + "post-deployment checks on Validation.",
            "Complete it. A final snapshot is taken, so what actually shipped is on the record "
            + "beside what was promised at the freeze.",
          ],
        },
      ],
      tables: [
        {
          title: "Release type",
          intro:
            "Chosen, never guessed from the version number. Type decides which approvals and "
            + "tests apply, so 2.1 being called a minor release is a decision somebody makes.",
          options: optionsFrom(RELEASE_TYPE_LABELS, {
            MAJOR: "Significant new capability. The widest set of approvals.",
            MINOR: "New features, backwards compatible.",
            PATCH: "Fixes only.",
            HOTFIX: "An urgent fix to production, outside the normal cycle.",
            EMERGENCY: "An incident response. Fewest approvers, all senior.",
            UPGRADE: "A version bump of something you depend on.",
            INFRASTRUCTURE: "Environment or platform change, no application code.",
            CONFIGURATION: "Settings only.",
          }),
        },
        {
          title: "Release status",
          intro: "Set by named moves, never chosen from a list.",
          options: optionsFrom(RELEASE_STATUS_LABELS, {
            DRAFT: "Just created. Nothing is committed to.",
            PLANNING: "Scope is being decided and can change freely.",
            SCOPE_FROZEN: "Contents agreed. Changes now need a coded reason.",
            VALIDATING: "The gates are being checked.",
            READY: "Every blocking gate passed. Not a person's opinion — the checks say so.",
            APPROVED: "The named approvers have said yes.",
            SCHEDULED: "Has a slot.",
            DEPLOYING: "Going out now.",
            DEPLOYED: "Out, not yet verified.",
            VERIFYING: "Being watched and checked after the fact.",
            COMPLETED: "Finished. A final snapshot is taken at this point.",
            ON_HOLD: "Paused deliberately.",
            FAILED: "Went out and did not work.",
            ROLLED_BACK: "Went out and was taken back.",
            CANCELLED: "Abandoned before shipping.",
            ARCHIVED: "Closed and out of the way.",
          }),
        },
      ],
      notes: [
        "The version is free text. Nothing parses it — 2.1, R2026.08 and August-Production are "
        + "all fine.",
        "The buttons offered come from the server, so a move you can see is a move that is "
        + "allowed. Hovering one tells you what it will do.",
      ],
    },

    {
      id: "release-scope",
      title: "Scope",
      where: "A release's Scope tab",
      purpose:
        "What the release carries. Scope moves freely until you freeze it; afterwards every "
        + "change carries a coded reason and lands in a ledger. Pretending scope never changes is "
        + "worse than admitting it does — teams work around a system that will not let them add "
        + "an emergency fix, and then the record shows nothing at all.",
      examples: [
        {
          title: "An emergency fix after the freeze",
          steps: [
            "The scope was frozen on Monday. On Friday a customer hits a refund bug.",
            "Add the bug to the release. Because the scope is frozen, you must pick a reason — "
            + "\"Raised by a customer\" — and may add a sentence.",
            "It goes in. The Scope changes panel now shows it, marked After freeze, with your "
            + "reason and your name.",
            "At the release review, the difference between the freeze snapshot and what shipped "
            + "is exactly this list.",
          ],
        },
        {
          title: "Adding an epic's contents",
          steps: [
            "Choose Add work, pick the epic, and tick which kinds of its children you want.",
            "Explicit rows are written for each — the release does not silently follow the epic, "
            + "so adding a story to that epic next month does not change what 2.1 contains.",
          ],
        },
      ],
      tables: [
        {
          title: "How an item relates to a release",
          intro:
            "\"In a release\" is not one relationship. A bug can be shipped by 2.1, seen in 2.0 "
            + "and still affect 1.9 — one field would force all three into the wrong answer.",
          options: optionsFrom(RELATION_LABELS, {
            PRIMARY_TARGET:
              "This release is delivering it. An item can be the primary target of exactly one "
              + "release — the database guarantees it.",
            ADDITIONAL_TARGET: "Also going out in this release, alongside its main one.",
            AFFECTED_RELEASE: "This release has the problem. It is not fixing it.",
            ACTUAL_RELEASE: "Where it actually ended up, recorded after the fact.",
          }),
        },
        {
          title: "Reasons for changing scope",
          intro:
            "Asked whenever scope changes after a freeze, and whenever anything is removed. "
            + "\"Something else\" demands a sentence.",
          options: SCOPE_CHANGE_REASONS.map(([code, label]) => ({
            name: label,
            meaning:
              code === "OTHER"
                ? "For anything the list does not cover — and it insists you write what happened."
                : "Recorded on the release, and counted in the release report.",
          })),
        },
        {
          title: "Does this item need testing?",
          options: optionsFrom(QA_DISPOSITION_LABELS, {
            REQUIRED: "The QA gate counts it. The default for anything being delivered.",
            NOT_REQUIRED: "Excused, with a reason — the reason is shown where a result would be.",
            WAIVED: "Should have been tested and deliberately was not. Also needs a reason.",
          }),
        },
      ],
      notes: [
        "Removing work always needs a reason, frozen or not. A release simply becoming smaller "
        + "explains nothing.",
        "Removed items are not deleted. The Removed from this release panel keeps them.",
      ],
    },

    {
      id: "readiness",
      title: "Readiness and gates",
      where: "A release's Overview, right-hand panel",
      purpose:
        "Twelve named checks, each with a sentence. Deliberately not a percentage — \"92% ready\" "
        + "tells nobody what to go and do, whereas \"QA: Fail — 1 QA-required ticket has failed\" "
        + "does.",
      examples: [
        {
          title: "Shipping over a failed gate",
          steps: [
            "A ticket failed QA, so the QA gate is red and the release cannot start.",
            "The team decides to ship anyway. Someone with override rights presses Accept a risk, "
            + "picks the gate and writes why it is acceptable.",
            "The gate still shows Fail, with the waiver beside it. The release header reads "
            + "Released with waiver, never Ready.",
            "That distinction is the point: \"we shipped this cleanly\" and \"we shipped this "
            + "over a failed QA gate\" must not look the same afterwards.",
          ],
        },
      ],
      tables: [
        {
          title: "Overall readiness",
          options: optionsFrom(READINESS_LABELS, {
            NOT_EVALUATED: "Nobody has checked yet. Not the same as failing.",
            READY: "Every blocking gate passed on its own merits.",
            READY_WITH_WAIVER: "A gate failed and somebody accepted the risk, on the record.",
            NOT_READY: "At least one blocking gate says no.",
          }),
        },
        {
          title: "What each gate asks",
          options: [
            { name: "Scope", meaning: "Is there work in this release, and is it settled?" },
            { name: "Development", meaning: "Has every mandatory item finished development?" },
            { name: "QA", meaning: "Did everything that needed testing pass, or get waived?" },
            {
              name: "Critical bugs",
              meaning:
                "Any unaccepted blocker or critical known issue? Accepting the risk is what "
                + "clears this, not deleting the issue.",
            },
            { name: "Pull requests", meaning: "Required merges done. Recorded by hand." },
            { name: "Build", meaning: "The chosen build succeeded and an artifact exists." },
            { name: "Prerequisites", meaning: "Every mandatory preparation step complete." },
            { name: "Rollback plan", meaning: "A plan exists where one is required." },
            { name: "Approvals", meaning: "Everyone who has to say yes has said it." },
            { name: "Documents", meaning: "Every required document actually exists." },
            { name: "Required fields", meaning: "Your organisation's own questions are answered." },
            { name: "Deployment window", meaning: "There is a date, and now is inside it." },
            {
              name: "Execution",
              meaning:
                "Every planned runbook step ran. About finishing, not starting — it has nothing "
                + "to say before a deployment begins.",
            },
            {
              name: "Post-deployment validation",
              meaning: "The checks passed and the observation window has elapsed.",
            },
          ],
        },
        {
          title: "What a gate can say",
          options: [
            { name: "Pass", meaning: "Checked and satisfied." },
            { name: "Fail", meaning: "Checked and not satisfied. The sentence says what to fix." },
            {
              name: "Not applicable",
              meaning:
                "Nothing to judge. Never reported as a pass — a green tick nobody earned is worse "
                + "than an honest blank.",
            },
            { name: "Waived", meaning: "Failed, and somebody accepted the risk in writing." },
          ],
        },
      ],
      notes: [
        "Re-check re-runs every gate and stores the answer. The release list reads that stored "
        + "answer, so it stays in step with the detail page.",
        "A waiver never rewrites the gate result. Both facts survive: the check failed, and a "
        + "named person went ahead.",
      ],
    },

    {
      id: "prerequisites",
      title: "Pre-requisites",
      where: "A release's Pre-requisites tab",
      purpose:
        "What has to be true before deployment starts — a database snapshot taken, access "
        + "granted, a maintenance window booked. Mandatory ones hold the release.",
      examples: [
        {
          title: "A typical checklist",
          steps: [
            "Add \"Production database snapshot taken\", category Backup, mandatory.",
            "The person who takes it marks it Complete and can attach evidence.",
            "If it turns out not to apply this time, Skipped needs a reason — a skipped step with "
            + "no explanation is the one an incident review asks about.",
          ],
        },
      ],
      tables: [
        {
          title: "Prerequisite status",
          options: optionsFrom(PREREQUISITE_STATUS_LABELS, {
            NOT_STARTED: "Nobody has done it yet.",
            IN_PROGRESS: "Being done now.",
            COMPLETED: "Done.",
            FAILED: "Attempted and did not work. Holds the release.",
            BLOCKED: "Cannot proceed because of something else.",
            SKIPPED: "Deliberately not done, with a reason.",
          }),
        },
      ],
    },

    {
      id: "testing",
      title: "Testing and QA sign-off",
      where: "A release's Testing tab, and QA on each scope row",
      purpose:
        "Two different questions, both asked. Whether every ticket that needed testing passed, "
        + "and whether the QA lead considers the release fit to ship — a release can be green on "
        + "every row and still be one somebody with the whole picture holds back.",
      examples: [
        {
          title: "Recording a failure",
          steps: [
            "On the Scope tab, press Record QA against the ticket.",
            "Choose Failed. A comment is required — \"Failed\" on its own gives the developer "
            + "nothing to act on.",
            "The QA gate turns red immediately and names the ticket.",
            "When it is fixed and retested, record Passed. The gate clears itself.",
          ],
        },
      ],
      tables: [
        {
          title: "Per-ticket QA result",
          options: optionsFrom(QA_STATUS_LABELS, {
            NOT_STARTED: "No test result yet.",
            IN_PROGRESS: "Being tested.",
            PASSED: "Tested and working.",
            FAILED: "Tested and broken. Needs a comment saying what went wrong.",
            BLOCKED: "Cannot be tested — environment down, dependency missing.",
          }),
        },
        {
          title: "Release-level sign-off",
          intro: "The QA lead's verdict on the release as a whole.",
          options: optionsFrom(TEST_STATUS_LABELS, {
            NOT_STARTED: "No verdict yet.",
            IN_PROGRESS: "Testing is under way.",
            PASSED: "Fit to ship.",
            PASSED_WITH_WAIVER: "Fit to ship despite something that failed, on the record.",
            FAILED: "Not fit to ship.",
          }),
        },
        {
          title: "Kinds of testing",
          intro:
            "Which kinds this release needs, and how each went. Required and done are separate "
            + "questions — a release where performance testing was never required reads very "
            + "differently from one where it was required and nobody ran it.",
          options: [
            { name: "Smoke", meaning: "Does it start and do the obvious things work?" },
            { name: "Functional", meaning: "Does each feature behave as specified?" },
            { name: "Regression", meaning: "Did anything that used to work stop working?" },
            { name: "Integration", meaning: "Do the parts still work together?" },
            { name: "UAT", meaning: "Does the customer accept it?" },
            { name: "Performance", meaning: "Is it fast enough under load?" },
            { name: "Security", meaning: "Scans, penetration testing, access checks." },
            { name: "Migration", meaning: "Does the data survive the upgrade?" },
            { name: "Compatibility", meaning: "Browsers, devices, versions you support." },
          ],
        },
      ],
      notes: [
        "Signing off as passed while QA-required tickets have failed is refused. Use a waiver — "
        + "that puts the same decision on the record instead of hiding it.",
      ],
    },

    {
      id: "approvals",
      title: "Approvals",
      where: "A release's Approvals tab",
      purpose:
        "Who has to say yes, and whether they have. The seats created depend on the release "
        + "type — a patch needs fewer than a major release.",
      examples: [
        {
          title: "Getting a release approved",
          steps: [
            "Press Request approvals. The seats for this release type are created and the named "
            + "people are told.",
            "Press Assign against a seat to name who is being asked. Until somebody is named, "
            + "anyone with approval rights could answer it — naming them is what makes the "
            + "sign-off accountable.",
            "The approver presses Approve, or Refuse with a reason. Refusing without saying why "
            + "is not allowed: it leaves the team nothing to fix.",
          ],
        },
      ],
      tables: [
        {
          title: "Approval status",
          options: optionsFrom(APPROVAL_STATUS_LABELS, {
            WAITING: "Asked, not yet answered.",
            APPROVED: "Given.",
            REJECTED: "Refused, with a reason.",
          }),
        },
        {
          title: "Approval roles",
          intro:
            "A seat at this release's table, not a system role. Being QA lead on one release "
            + "gives you nothing on any other.",
          options: [
            { name: "QA lead", meaning: "Answers for testing." },
            { name: "Technical lead", meaning: "Answers for the code and the design." },
            { name: "Project manager", meaning: "Answers for scope and delivery." },
            { name: "Release manager", meaning: "Answers for the process." },
            { name: "DevOps", meaning: "Answers for the deployment and the environment." },
            { name: "Security", meaning: "Answers for security review." },
            { name: "Business owner", meaning: "Answers for whether it is wanted." },
            { name: "Customer UAT", meaning: "The customer's acceptance." },
            { name: "Change manager", meaning: "Answers for the change process — CAB and similar." },
          ],
        },
      ],
      notes: [
        "An approval that has been answered stays on the record and cannot be removed.",
        "Only the named approver can answer once one is named.",
      ],
    },

    {
      id: "execution",
      title: "Execution and deployments",
      where: "A release's Execution tab",
      purpose:
        "The runbook — what actually gets done on the night — and a record of each attempt to "
        + "deploy. Shown as a list grouped by phase rather than a stepper, because during a "
        + "deployment several people work different steps at once.",
      examples: [
        {
          title: "A deployment that took two goes",
          steps: [
            "Work down the Prepare steps: snapshot, maintenance page, scale down.",
            "Press Start a deployment, choose Production, enter the artifact version and the "
            + "pipeline run so the logs can be found later.",
            "The migration times out. Record the outcome as Failed with what went wrong — a "
            + "failure with a blank there is where the question is.",
            "Fix it, then Start a deployment again. This is attempt 2, recorded separately: a "
            + "retry never overwrites the attempt that failed.",
            "The release report later shows Production at two attempts per success, which is the "
            + "number worth acting on.",
          ],
        },
      ],
      tables: [
        {
          title: "Runbook phases",
          options: optionsFrom(EXECUTION_PHASE_LABELS, {
            PREPARE: "Before anything changes — backups, notifications, scaling.",
            DEPLOY: "The change itself.",
            VERIFY: "Immediate checks that it worked.",
            FINALISE: "Tidying up — re-enable jobs, remove the maintenance page.",
          }),
        },
        {
          title: "Step status",
          options: optionsFrom(STEP_STATUS_LABELS, {
            NOT_STARTED: "Waiting.",
            READY: "Everything it waits for is done.",
            IN_PROGRESS: "Being run now.",
            COMPLETED: "Done.",
            FAILED: "Ran and did not work. Needs a reason.",
            BLOCKED: "Waiting on something that has not finished.",
            SKIPPED: "Deliberately not run, with a reason.",
          }),
        },
        {
          title: "If this step fails…",
          intro: "Decided in advance, in daylight, rather than at two in the morning.",
          options: optionsFrom(ON_FAILURE_LABELS, {
            CONTINUE: "Not important enough to stop for.",
            STOP_RELEASE: "Halt. Do not carry on.",
            TRIGGER_ROLLBACK: "This is bad enough to consider going back.",
            MANUAL_DECISION: "A person judges it on the night.",
          }),
        },
        {
          title: "Deployment outcome",
          options: optionsFrom(DEPLOYMENT_STATUS_LABELS, {
            PLANNED: "Intended, not started.",
            IN_PROGRESS: "Running now. Only one at a time per environment.",
            SUCCEEDED: "Worked.",
            FAILED: "Did not work. Needs a reason.",
            ROLLED_BACK: "Went out and was taken back.",
            CANCELLED: "Called off.",
          }),
        },
      ],
      notes: [
        "Never put a password, key or token in an instruction. Reference where the secret lives "
        + "— \"use secret prod/payment-db from Vault\". The application refuses text that looks "
        + "like a credential.",
        "Steps can only be reordered before anything has run. Afterwards the numbers are the "
        + "order things actually happened in.",
        "Pipeline run is a link or reference to the CI/CD job — a Jenkins build number, a GitHub "
        + "Actions run. Nothing is fetched from it; it is so the logs can be found afterwards.",
      ],
    },

    {
      id: "rollback",
      title: "Rollback",
      where: "A release's Rollback tab",
      purpose:
        "How you would undo this, written before you need it. A rollback plan invented during an "
        + "incident is written by people under pressure who have already had a bad night.",
      examples: [
        {
          title: "Writing a plan you hope not to use",
          steps: [
            "Record the decision owner — who is allowed to call it — and how long it takes.",
            "Add the steps in order: restore the previous image, run the down migration, clear "
            + "the cache.",
            "If it is genuinely not needed, mark the plan exempt with a reason rather than "
            + "leaving it blank. A blank plan and a plan nobody needs look identical otherwise.",
          ],
        },
      ],
      tables: [
        {
          title: "Rollback step status",
          options: optionsFrom(ROLLBACK_STATUS_LABELS, {
            NOT_STARTED: "Not run.",
            IN_PROGRESS: "Running.",
            COMPLETED: "Done.",
            FAILED: "Did not work — which during a rollback is the worst news there is.",
            SKIPPED: "Not needed on the night.",
          }),
        },
      ],
    },

    {
      id: "validation",
      title: "Post-deployment validation",
      where: "A release's Validation tab",
      purpose:
        "Whether it actually worked, and whether anybody has watched it long enough. Kept apart "
        + "from execution because a deployment can run every step perfectly and still have broken "
        + "something.",
      examples: [
        {
          title: "Finishing a release honestly",
          steps: [
            "After deploying, press Add the usual checks — health, smoke test, error rate.",
            "Record each as Pass or Fail. A failure needs a reason.",
            "Set an observation window — say 60 minutes. The release cannot be completed until it "
            + "has elapsed, counted from a server clock so everyone sees the same countdown.",
            "When the window closes and the checks are green, Complete the release.",
          ],
        },
      ],
      tables: [
        {
          title: "Check result",
          options: optionsFrom(VALIDATION_STATUS_LABELS, {
            NOT_STARTED: "Nobody has looked yet.",
            IN_PROGRESS: "Being checked.",
            PASSED: "Confirmed working.",
            FAILED: "Confirmed broken. Holds completion.",
            SKIPPED: "Not applicable this time, with a reason.",
          }),
        },
      ],
    },

    {
      id: "known-issues",
      title: "Known issues",
      where: "A release's Known issues tab",
      purpose:
        "Things known to be broken that are shipping anyway. Doing that is a legitimate and "
        + "common decision; doing it with something nobody named is not.",
      examples: [
        {
          title: "Shipping with a known blocker",
          steps: [
            "Record the issue: \"Card payments fail on gateway B\", severity Blocker, with the "
            + "impact and any workaround.",
            "The critical-bugs gate immediately fails and names it.",
            "Someone with override rights presses Accept risk and writes why it is acceptable — "
            + "\"Gateway B is 2% of volume and the fix ships Monday\".",
            "The gate clears. The issue stays on the release for ever, and appears in the release "
            + "notes under Known limitations, because the person it is usually undisclosed to is "
            + "the customer.",
          ],
        },
      ],
      tables: [
        {
          title: "Severity, and what it costs you",
          options: optionsFrom(SEVERITY_LABELS, {
            BLOCKER: "Holds the release until somebody accepts the risk.",
            CRITICAL: "Also holds the release.",
            MAJOR: "Recorded, does not hold the release.",
            MINOR: "Recorded only.",
            TRIVIAL: "Recorded only.",
          }),
        },
      ],
      notes: [
        "Once a risk is accepted the issue cannot be reworded or deleted. Somebody accepted a "
        + "specific risk in specific words, and letting the words change afterwards would make "
        + "their acceptance apply to something they never read.",
      ],
    },

    {
      id: "documents",
      title: "Documents and release notes",
      where: "A release's Documents tab",
      purpose:
        "What the release must produce, and whether it exists. Requirements and documents are one "
        + "list, because \"has no rollback plan\" and \"needs no rollback plan\" have to be "
        + "distinguishable.",
      examples: [
        {
          title: "A document that lives in Confluence",
          steps: [
            "Require a Deployment plan, mandatory. The documents gate goes red.",
            "The plan already exists in the wiki, so use Link one and paste the URL rather than "
            + "uploading a copy that will go stale.",
            "The gate clears. The link is on the release for anyone who needs it.",
          ],
        },
        {
          title: "Release notes",
          steps: [
            "The notes are assembled from the scope — grouped into Highlights, New and improved, "
            + "Changes and Fixes, with known limitations at the end.",
            "Work only observed in this release is left out, so the notes never claim a fix that "
            + "did not happen.",
            "Edit the text — ticket titles are written for the team, and \"Fix NPE in "
            + "OrderMapper\" is not a sentence to show a customer.",
            "Save as document. It becomes a real file on the release, so \"these are the notes we "
            + "shipped\" means something.",
          ],
        },
      ],
      tables: [
        {
          title: "Kinds of document",
          options: [
            { name: "Release notes", meaning: "What changed, for the people receiving it." },
            { name: "Deployment plan", meaning: "How it goes out." },
            { name: "Test plan / Test report", meaning: "What was tested, and what happened." },
            { name: "UAT report", meaning: "The customer's acceptance testing." },
            { name: "Rollback plan", meaning: "How to undo it." },
            { name: "Database script / Migration document", meaning: "What happens to the data." },
            { name: "Architecture document", meaning: "What the change does structurally." },
            { name: "Runbook / Operational guide", meaning: "How to run and support it." },
            { name: "Approval document", meaning: "Evidence of a sign-off." },
            { name: "Change request / CAB approval", meaning: "Your change-management paperwork." },
            { name: "Customer approval", meaning: "The customer's written yes." },
            { name: "User guide / Training document", meaning: "For the people who will use it." },
            { name: "Known issues", meaning: "A separate document, where you keep one." },
          ],
        },
      ],
      notes: [
        "Remove file takes the document away and leaves the requirement, so the gate goes back to "
        + "failing — which is honest: the release still needs it and no longer has it.",
        "A required document slot cannot be deleted. Make it optional first if it genuinely is.",
      ],
    },

    {
      id: "modules",
      title: "Modules and versions",
      where: "Administration → Modules · a project's Modules tab · the release page",
      purpose:
        "A product built from parts that ship separately. PR Blocks is one product; Inventory, "
        + "Production and Accounting each have their own version and move on their own schedules. "
        + "\"PR Blocks 5.0\" is a statement about which versions of those go together, and the "
        + "release page records exactly that.",
      examples: [
        {
          title: "Setting it up once",
          steps: [
            "Administration → Module types: Standalone and Customisable come as standard. Add "
            + "your own — Third-party, Library, Infrastructure — whatever your estate has.",
            "The only setting a type carries is whether its version is shared across projects. "
            + "Tick it for software several customers run; leave it for something built for one.",
            "Administration → Modules: add Inventory, Production, Accounting, each with a type.",
            "On each project's Modules tab, link the ones it has and set the version that "
            + "project is running today — not the newest, what is actually installed there.",
          ],
        },
        {
          title: "PR Blocks 5.0 — a whole-product release",
          steps: [
            "New release, \"This is a release of\" → the whole product. Version 5.0.",
            "The Modules card opens with every module at what the project runs now, nothing "
            + "marked as changing. Creating a release is not a decision to upgrade anything.",
            "Set Accounting to 2.1 and tick \"This release upgrades it\". Inventory and "
            + "Production are left alone and show as carried.",
            "The card now reads: Inventory 3.8 → 3.8, Production 4.0 → 4.0, Accounting 2.0 → 2.1.",
            "Press Apply. PR Blocks is now recorded as running Accounting 2.1.",
          ],
        },
        {
          title: "Inventory 3.9 — one module, product number unchanged",
          steps: [
            "Only Inventory changed, and PR Blocks should stay at 5.0.",
            "New release, \"This is a release of\" → one module of this project → Inventory. "
            + "The version you type is Inventory's, not the product's.",
            "The manifest carries one line: Inventory 3.8 → 3.9.",
            "PR Blocks stays 5.0 and now runs Inventory 3.9, Production 4.0, Accounting 2.1.",
          ],
        },
        {
          title: "Accounting 2.2 — a shared component",
          steps: [
            "Accounting is used by several customers, so this is a component release.",
            "New release → a shared component → Accounting, version 2.2.",
            "\"Who else runs this\" appears on the release, listing every other project holding "
            + "Accounting and which version each is on. That is the blast radius.",
            "Applying it records Accounting 2.2 as the module's latest release, and moves this "
            + "project to 2.2. Every other project stays where it was and reads \"behind\" — "
            + "because nobody has deployed it for them yet.",
          ],
        },
      ],
      tables: [
        {
          title: "The three kinds of release",
          intro:
            "Chosen when you create the release. It decides what the version number belongs to.",
          options: [
            {
              name: "Whole product",
              meaning:
                "Carries a manifest of every module. The only kind that moves the product number.",
            },
            {
              name: "Module release",
              meaning:
                "One module belonging to this project. Its version moves; the product number "
                + "does not.",
            },
            {
              name: "Component release",
              meaning:
                "A shared module, raised against one project but felt wherever it is used. The "
                + "product number does not move.",
            },
          ],
        },
        {
          title: "What a module type decides",
          intro: "One setting, and it is the only behaviour a type carries.",
          options: [
            {
              name: "Version shared across projects",
              meaning:
                "Releasing it records a new version of the software itself, so every project "
                + "holding it can be compared against that — which is what lets a customer read "
                + "\"behind\". Right for anything several customers run.",
            },
            {
              name: "Version per project",
              meaning:
                "The version belongs to one customer's installation. There is nothing shared to "
                + "be behind, and releasing it affects nobody else.",
            },
          ],
        },
      ],
      notes: [
        "Releasing a shared module is not the same as deploying it. Applying a manifest moves "
        + "this project and the module's own latest version; every other project stays where it "
        + "was until it actually goes out there. A page that showed them all on the new version "
        + "would be claiming deployments nobody did.",
        "\"This release upgrades it\" is a decision, not a comparison. Tick it even when the "
        + "number is unchanged, if the module was rebuilt after a bad build and has to be "
        + "deployed again — only ticked modules move when the manifest is applied.",
        "Module versions are free text, like release versions. The system can say whether a "
        + "project is on the latest, but not how far behind — there is no version parser.",
        "Two modules can be released at the same number: Inventory 3.9 and Production 3.9 are "
        + "different releases. The product number stays unique on its own.",
        "The manifest is settled once scope is frozen, along with everything else about what the "
        + "release contains.",
      ],
    },
    {
      id: "templates",
      title: "Release templates",
      where: "Release Templates in the left menu",
      purpose:
        "What a release of each kind is held to — its prerequisites, approvals, runbook, rollback "
        + "plan, checks and documents. Versioned, so changing a template never rewrites the "
        + "releases already built from it.",
      examples: [
        {
          title: "Changing the standard production process",
          steps: [
            "Open the template. The active version is read-only, with a lock.",
            "Press New draft. Edit that — add a security approval, say.",
            "Activate it. New releases use V2; the ones already created keep the V1 governance "
            + "they were made under, because that is what they were held to.",
          ],
        },
      ],
      tables: [
        {
          title: "Version status",
          options: [
            { name: "Draft", meaning: "Editable. Not used by anything yet." },
            { name: "Active", meaning: "What new releases copy. Read-only." },
            { name: "Retired", meaning: "Superseded. Kept because past releases used it." },
          ],
        },
      ],
      notes: [
        "A template can say which kind of release it is for, and which module. A component "
        + "release of Accounting then offers Accounting's runbook first, ahead of anything more "
        + "general — the same steps, every customer, every time. A template naming no scope still "
        + "applies everywhere.",
        "A template with no active version cannot be used to create anything — the list flags it.",
        "Withdraw stops a template being offered without touching the releases built from it.",
      ],
    },

    {
      id: "release-reports",
      title: "Release reports",
      where: "Below the release list on a project's Releases tab",
      purpose:
        "How the team actually ships. Every figure is one somebody could act on, and every rate "
        + "is shown with what it was measured over — 100% across one release and across forty are "
        + "different facts.",
      examples: [
        {
          title: "Reading it honestly",
          steps: [
            "Released cleanly counts only releases that finished. One still in flight has not "
            + "succeeded or failed yet.",
            "A dash rather than 0% means nothing has settled — not that everything failed.",
            "Deployment reliability shows attempts per success. One is healthy; three means the "
            + "deployment is unreliable and a report counting only successes would never show it.",
            "Gates waived repeatedly is worth reading as a process that does not fit, rather than "
            + "a team that keeps cutting corners.",
          ],
        },
      ],
      tables: [],
    },
  ],
};
